import axios, { AxiosInstance, AxiosResponse } from 'axios'
import moment from 'moment'

import { Users } from './Users'
import { LoginRequest, MaskRequest, SetPasswordRequest, TokenResponseType } from './types'
import { CustomAxiosConfig } from '../client/types'

const ACCESS_TOKEN_TIMEOUT = 5 * 60
const REFRESH_TOKEN_TIMEOUT = 24 * 60 * 60
const KIOSK_AUTH_STORAGE_KEY = "kioskAuthMetadata"

export type Callback = () => void

interface ForgotPasswordDataType {
  email: string
}

interface ValidateTokenDataType {
  token: string
}

interface ResetPasswordType {
  password: string
  token: string
}

interface IToken {
  token: string
  expires: Date
}

interface IAuth {
  access: IToken
  refresh: IToken
  accountType: string
  isStaff: boolean
  isSuperuser: boolean
}

interface IIdentity {
  user?: IAuth
  masker?: IAuth
}

export enum KioskScope {
  DriverCheckIn = "kiosk.driver_check_in"
}

interface KioskAuthMetadata {
  facility: {
    guid: string
    name: string
  }
  supplyChain: {
    guid: string
    name: string
  }
  scopes: KioskScope[]
  expiresOn: Date
}

const createKioskMetadata = (response: AxiosResponse): KioskAuthMetadata => {
  return {
    supplyChain: {
      name: response.data.supply_chain.name,
      guid: response.data.supply_chain.xid,
    },
    facility: {
      name: response.data.facility.name,
      guid: response.data.facility.xid,
    },
    scopes: response.data.scopes,
    expiresOn: moment(response.data.expires_on).toDate(),
  }
}

const storeKioskAuthMetadata = (metadata: KioskAuthMetadata) => {
  localStorage.setItem(KIOSK_AUTH_STORAGE_KEY, JSON.stringify(metadata))
}

const loadKioskAuthMetadata = () => {
  const metadataJson = localStorage.getItem(KIOSK_AUTH_STORAGE_KEY)

  if (!metadataJson) return null
  const {expiresOn, ...metadata} = JSON.parse(metadataJson)

  return {
    ...metadata,
    expiresOn: moment(expiresOn).toDate()
  }
}

const createAuth = (response: AxiosResponse<TokenResponseType>): IAuth => {
  return {
    access: {
      token: response.data.access,
      expires: moment().add(ACCESS_TOKEN_TIMEOUT, 'seconds').toDate(),
    },
    refresh: {
      token: response.data.refresh,
      expires: moment().add(REFRESH_TOKEN_TIMEOUT, 'seconds').toDate(),
    },
    accountType: response.data.type,
    isStaff: response.data.is_staff,
    isSuperuser: response.data.is_superuser,
  }
}

const refreshAuth = (auth: IAuth, response: AxiosResponse<TokenResponseType>): IAuth => {
  auth.access.expires = moment().add(ACCESS_TOKEN_TIMEOUT, 'seconds').toDate()
  auth.access.token = response.data.access
  return auth
}

const storeIdentity = (identity: IIdentity) => {
  localStorage.setItem('skuit', JSON.stringify(identity))
}

const loadIdentity = (): IIdentity => {
  const jsonIdentity = localStorage.getItem('skuit')
  if (jsonIdentity) {
    const identity = JSON.parse(jsonIdentity)
    if (identity.user) {
      identity.user.access.expires = new Date(identity.user.access.expires)
      identity.user.refresh.expires = new Date(identity.user.refresh.expires)
    }
    if (identity.masker) {
      identity.masker.access.expires = new Date(identity.masker.access.expires)
      identity.masker.refresh.expires = new Date(identity.masker.refresh.expires)
    }
    return identity
  }
  return {
    user: undefined,
    masker: undefined,
  }
}

export class Auth {
  _authAxios: AxiosInstance
  callbacks: Callback[]
  identity: IIdentity
  kioskAuth?: KioskAuthMetadata

  constructor() {
    this._authAxios = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })
    this._authAxios.interceptors.request.use((config) => {
      const token = this.getAccessToken()
      if (token) {
        config.headers.authorization = `Bearer ${token}`
      }
      return config
    })
    this.identity = loadIdentity()
    this.kioskAuth = loadKioskAuthMetadata()
    this.callbacks = []

    // Sync in-memory auth state when another tab (e.g. the v2 app) updates
    // localStorage. Without this, after a cross-tab token refresh both apps
    // would try to use the same refresh token, causing one to fail.
    window.addEventListener('storage', (event) => {
      if (event.key === 'skuit') {
        this.identity = loadIdentity()
      }
    })
  }

  registerCallback = (cb: Callback) => {
    this.callbacks.push(cb)
  }

  unregisterCallback = (cb: () => void) => {
    const index = this.callbacks.indexOf(cb)
    if (index > -1) {
      this.callbacks.splice(index, 1)
    }
  }

  login = async (data: LoginRequest) => {
    const response = await this._authAxios?.post('/auth/token/', data)
    this.identity.user = createAuth(response)
    storeIdentity(this.identity)
    for (let i = 0; i < this.callbacks.length; i += 1) {
      this.callbacks[i]()
    }
  }

  setPassword = async (data: SetPasswordRequest) => {
    const response = await this._authAxios?.put('/auth/set-password', data)
    return response
  }

  logout = () => {
    this.identity.user = undefined
    this.identity.masker = undefined
    storeIdentity(this.identity)
    for (let i = 0; i < this.callbacks.length; i += 1) {
      this.callbacks[i]()
    }
  }

  refreshAccessToken = async () => {
    if (!this.isAuthed()) {
      this.logout()
      return
    }
    if (
      this.identity.user &&
      this.identity.user.access.expires &&
      this.identity.user.access.expires.getTime() > Date.now()
    ) {
      return
    }
    const response = await this._authAxios?.post('/auth/token/refresh/', {
      refresh: this.identity.user?.refresh.token,
    })
    this.identity.user = refreshAuth(this.identity.user as IAuth, response)
    storeIdentity(this.identity)
  }

  getAccessToken = () => {
    return this.identity.user?.access.token
  }

  getAccountType = () => {
    return this.identity.user?.accountType
  }

  isAuthed = (): boolean => {
    return !!this.identity.user && !!this.identity.user.refresh.token && this.identity.user.refresh.expires.getTime() > Date.now()
  }

  get users() {
    const api = new Users(this._authAxios)
    Object.defineProperty(this, 'users', { value: api })
    return api
  }

  mask = async (data: MaskRequest): Promise<AxiosResponse<TokenResponseType>> => {
    const response = await this._authAxios.post('/auth/mask/', data)

    this.identity.masker = this.identity.user
    this.identity.user = createAuth(response)
    storeIdentity(this.identity)
    return response
  }

  unMask = (): void => {
    this.identity.user = this.identity.masker
    this.identity.masker = undefined
    storeIdentity(this.identity)
  }

  authorizeKiosk = async (data: {scopes: string[], facility: string}, config: CustomAxiosConfig = {}): Promise<any> => {
    const axiosConfig = {
      ...config,
      withCredentials: true
    }
    const response =  await this._authAxios.post(
      `/auth/kiosk/token`,
      data,
      axiosConfig
    )

    const metadata = createKioskMetadata(response)
    storeKioskAuthMetadata(metadata)
    this.kioskAuth = metadata

    return response
  }

  revokeKioskAuth = async () => {
    await this._authAxios.post(`/auth/kiosk/token/revoke`,null, {withCredentials: true} )
    localStorage.removeItem(KIOSK_AUTH_STORAGE_KEY)
    this.kioskAuth = undefined
  }

  isMasked = (): boolean => {
    return !!this.identity.masker && this.identity.masker.refresh.expires.getTime() > Date.now()
  }

  isStaff = (): boolean => {
    return this.identity.user?.isStaff || false
  }

  isSuperUser = (): boolean => {
    return this.identity.user?.isSuperuser || false
  }

  forgotPassword = async (data: ForgotPasswordDataType) => {
    const response = await this._authAxios?.post('/auth/password_reset/', data)
    return response
  }

  validateResetToken = async (data?: ValidateTokenDataType) => {
    const response = await this._authAxios?.post('/auth/password_reset/validate_token/', data)
    return response
  }

  resetPassword = async (data: ResetPasswordType) => {
    const response = await this._authAxios?.post('/auth/password_reset/confirm/', data)
    return response
  }
}
